import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LANGUAGES, TranslationService } from './service/translation-service.service';
import { TourService } from '../tour/tour.service';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

export const wikiUrl = environment.wikiBaseUrl;
export const wikiPath = '/wiki';
const wikiHelpWindowName = '_supplycanvas_wiki';
let wikiHelpWindow: Window | null = null;

@Component({
  selector: 'app-info-text',
  template: `
    <p class="mb-0px" [style.margin]="paragraphMargin">
      <ng-content></ng-content>
    </p>
    @if (tour) {
      <button type="button" (click)="openTour()" title="{{ 'start_tour_tr' | translate }}" mat-stroked-button>
        <mat-icon>play_circle</mat-icon>
      </button>
    }
    @if (url) {
      <button
        type="button"
        (click)="openHelp()"
        title="{{ 'Open_Wiki_Help_tr' | translate }}"
        [attr.aria-label]="'Open_Wiki_Help_tr' | translate"
        [class.square-icon-button]="iconOnly"
        [class.icon-only]="iconOnly"
        mat-stroked-button
      >
        <mat-icon>help</mat-icon>
      </button>
    }
  `,
  imports: [MatButton, MatIconModule, TranslateModule],
  styles: `
    :host {
      display: flex;
      flex-direction: row;
      gap: 10px;
      margin: 0 0 0;

      ::ng-deep .mdc-button {
        min-width: inherit;
        display: inline-flex;
      }

      ::ng-deep .mat-mdc-outlined-button > .mat-icon {
        margin: 0;
      }

      :host(.icon-only) {
        flex: 0 0 auto;
        width: 48px;
        height: 48px;
        gap: 0;
        align-items: center;
        justify-content: center;
      }

      :host(.icon-only) p {
        display: none;
      }

      :host(.icon-only) .square-icon-button {
        width: 48px;
        height: 48px;
        min-width: 48px;
        padding: 0;
      }
    }
    p {
      flex-grow: 1;
      text-align: justify;
      margin-bottom: 0px !important;
      align-self: center;
    }
  `,
})
export class InfoTextBoxComponent implements OnInit {
  private readonly injector = inject(Injector);
  @Input() url?: string;
  @Input() tour!: string;
  @Input() paragraphMargin = '0 1em 1em 0';
  @Input() iconOnly = false;
  private readonly translation = inject(TranslationService);

  ngOnInit(): void {
    if (!this.tour) {
      return;
    }
    void this.injector.get(TourService).maybeAutostartTourById(this.tour);
  }

  static openHelp(url: string, lang = 'en') {
    const width = 1200;
    const height = 736;
    const screenLeft = window.screenLeft ?? window.screenX;
    const screenTop = window.screenTop ?? window.screenY;
    const screenWidth = window.innerWidth ?? document.documentElement.clientWidth ?? screen.width;
    const screenHeight = window.innerHeight ?? document.documentElement.clientHeight ?? screen.height;

    const left = screenWidth / 2 - width / 2 + screenLeft;
    const top = screenHeight / 2 - height / 2 + screenTop;

    const wikiPrefix = url.startsWith(`${wikiPath}/`)
      ? wikiPath
      : url.startsWith(`${wikiUrl}/`)
        ? wikiUrl
        : undefined;
    const isBareWikiPath = url.startsWith('/') && !url.startsWith('//') && !wikiPrefix;
    if (wikiPrefix || isBareWikiPath) {
      const path = wikiPrefix ? url.slice(wikiPrefix.length) : url;
      const [pathWithoutFragment, fragment] = path.split('#', 2);
      const segments = pathWithoutFragment.split('/').filter(Boolean);
      const hasLocale = LANGUAGES.some(({ code }) => code === segments[0]);
      const localizedPath = [lang || 'en', ...(hasLocale ? segments.slice(1) : segments)].join('/');
      url = `${wikiUrl}/${localizedPath}${fragment ? `#${fragment}` : ''}`;
    }

    if (wikiHelpWindow && !wikiHelpWindow.closed) {
      wikiHelpWindow.location.replace(url);
      wikiHelpWindow.focus();
      return;
    }

    wikiHelpWindow = window.open(
      '',
      wikiHelpWindowName,
      `popup,width=${width},height=${height},left=${left},top=${top}`
    );

    if (wikiHelpWindow) {
      wikiHelpWindow.location.replace(url);
      wikiHelpWindow.focus();
    } else {
      alert('Please allow popups for this website');
    }
  }

  openHelp() {
    InfoTextBoxComponent.openHelp(this.url!, this.translation.language);
  }

  async openTour() {
    await this.injector.get(TourService).startTour(this.tour!);
  }
}
